"use client";

import { useRef } from "react";
import { motion, useInView } from "framer-motion";

function TypingIndicator({ y, delay, shouldAnimate }: { y: number; delay: number; shouldAnimate: boolean }) {
  return (
    <motion.g
      initial={{ opacity: 0 }}
      animate={shouldAnimate ? { opacity: [0, 1, 1, 0] } : { opacity: 0 }}
      transition={{ duration: 0.8, delay, times: [0, 0.1, 0.7, 1] }}
    >
      <rect x="6" y={y} width="14" height="6" rx="3" fill="#EDE9FE" />
      {[0, 1, 2].map((i) => (
        <motion.circle
          key={i}
          cx={10 + i * 3}
          cy={y + 3}
          r="1"
          fill="#8B5CF6"
          initial={{ opacity: 0.3 }}
          animate={shouldAnimate ? { opacity: [0.3, 1, 0.3] } : { opacity: 0.3 }}
          transition={{
            duration: 0.5,
            repeat: Infinity,
            delay: delay + i * 0.1,
          }}
        />
      ))}
    </motion.g>
  );
}

function ChatBubble({
  type,
  text,
  y,
  delay,
  shouldAnimate,
}: {
  type: string;
  text: string;
  y: number;
  delay: number;
  shouldAnimate: boolean;
}) {
  const isUser = type === "user";
  const bubbleWidth = Math.min(text.length * 2.2 + 8, 70);
  const x = isUser ? 90 - bubbleWidth : 6;

  return (
    <motion.g
      initial={{ opacity: 0, x: isUser ? 5 : -5 }}
      animate={shouldAnimate ? { opacity: 1, x: 0 } : { opacity: 0, x: isUser ? 5 : -5 }}
      transition={{ duration: 0.25, delay }}
    >
      <rect
        x={x}
        y={y}
        width={bubbleWidth}
        height="7"
        rx="3.5"
        fill={isUser ? "#8B5CF6" : "#EDE9FE"}
      />
      <text
        x={x + bubbleWidth / 2}
        y={y + 5}
        textAnchor="middle"
        fill={isUser ? "white" : "#5B21B6"}
        fontSize="3.5"
        fontWeight="500"
        fontFamily="system-ui"
      >
        {text}
      </text>
    </motion.g>
  );
}

function Sparkle({ x, y, delay, shouldAnimate }: { x: number; y: number; delay: number; shouldAnimate: boolean }) {
  return (
    <motion.g
      initial={{ opacity: 0, scale: 0 }}
      animate={
        shouldAnimate
          ? {
              opacity: [0, 1, 0],
              scale: [0.5, 1, 0.5],
              rotate: [0, 180],
            }
          : { opacity: 0, scale: 0 }
      }
      transition={{
        duration: 1.2,
        delay,
        repeat: Infinity,
        repeatDelay: 1,
      }}
    >
      <path
        d={`M${x},${y - 2} L${x + 0.8},${y} L${x},${y + 2} L${x - 0.8},${y} Z`}
        fill="#8B5CF6"
      />
      <path
        d={`M${x - 2},${y} L${x},${y + 0.8} L${x + 2},${y} L${x},${y - 0.8} Z`}
        fill="#8B5CF6"
      />
    </motion.g>
  );
}

export function AiAssistantIllustration() {
  const containerRef = useRef(null);
  const isInView = useInView(containerRef, { once: true, amount: 0.3 });

  return (
    <div 
      ref={containerRef}
      className="w-full h-full flex items-center justify-center p-2"
    >
      <svg viewBox="0 0 100 100" className="w-full h-full">
        <defs>
          <linearGradient id="aiBgGradient" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#EDE9FE" stopOpacity="0.4" />
            <stop offset="50%" stopColor="#F5F3FF" stopOpacity="0.2" />
            <stop offset="100%" stopColor="#EDE9FE" stopOpacity="0.4" />
          </linearGradient>

          <linearGradient id="headerGradient" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#8B5CF6" />
            <stop offset="100%" stopColor="#7C3AED" />
          </linearGradient>

          <filter id="buttonGlow" x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur stdDeviation="1" result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>

        {/* Background */}
        <rect width="100" height="100" fill="url(#aiBgGradient)" rx="8" />

        {/* Chat window */}
        <motion.g
          initial={{ opacity: 0, scale: 0.95 }}
          animate={isInView ? { opacity: 1, scale: 1 } : { opacity: 0, scale: 0.95 }}
          transition={{ duration: 0.3 }}
        >
          <rect
            x="4"
            y="4"
            width="92"
            height="92"
            rx="5"
            fill="black"
            fillOpacity="0.08"
          />
          <rect x="2" y="2" width="92" height="92" rx="5" fill="white" />

          {/* Header */}
          <rect
            x="2"
            y="2"
            width="92"
            height="11"
            rx="5"
            fill="url(#headerGradient)"
          />
          <rect x="2" y="8" width="92" height="5" fill="url(#headerGradient)" />

          <circle cx="10" cy="7.5" r="3" fill="white" fillOpacity="0.2" />
          <circle cx="10" cy="7.5" r="2" fill="white" />

          <text
            x="16"
            y="9.5"
            fill="white"
            fontSize="4"
            fontWeight="600"
            fontFamily="system-ui"
          >
            Buddy AI Assistant
          </text>

          <circle cx="86" cy="7.5" r="2" fill="#4ADE80" />
        </motion.g>

        {/* Conversation */}

        {/* 1. Shipping question */}
        <ChatBubble
          type="user"
          text="How fast is shipping?"
          y={17}
          delay={0.3}
          shouldAnimate={isInView}
        />
        <TypingIndicator y={26} delay={0.7} shouldAnimate={isInView} />
        <ChatBubble
          type="bot"
          text="2-3 business days! 📦"
          y={26}
          delay={1.1}
          shouldAnimate={isInView}
        />

        {/* 2. Returns question */}
        <ChatBubble 
          type="user" 
          text="What about returns?" 
          y={35} 
          delay={1.6} 
          shouldAnimate={isInView}
        />
        <TypingIndicator y={44} delay={2} shouldAnimate={isInView} />
        <ChatBubble
          type="bot"
          text="30-day easy returns ✓"
          y={44}
          delay={2.4}
          shouldAnimate={isInView}
        />

        {/* 3. Product question */}
        <ChatBubble 
          type="user" 
          text="Available in blue?" 
          y={53} 
          delay={2.9} 
          shouldAnimate={isInView}
        />
        <TypingIndicator y={62} delay={3.3} shouldAnimate={isInView} />
        <ChatBubble
          type="bot"
          text="Yes! 5 colors available 🎨"
          y={62}
          delay={3.7}
          shouldAnimate={isInView}
        />

        {/* 4. Discount question */}
        <ChatBubble
          type="user"
          text="Any discount available?"
          y={71}
          delay={4.2}
          shouldAnimate={isInView}
        />

        {/* Discount button appears */}
        <motion.g
          initial={{ opacity: 0, scale: 0.9 }}
          animate={isInView ? { opacity: 1, scale: 1 } : { opacity: 0, scale: 0.9 }}
          transition={{ delay: 5, duration: 0.3 }}
        >
          <motion.g
            initial={{ scale: 1 }}
            animate={isInView ? { scale: [1, 1.02, 1] } : { scale: 1 }}
            transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut", delay: 5.3 }}
            filter="url(#buttonGlow)"
          >
            <rect x="6" y="82" width="54" height="8" rx="4" fill="#8B5CF6" />
            <text
              x="33"
              y="87"
              textAnchor="middle"
              fill="white"
              fontSize="4"
              fontWeight="600"
              fontFamily="system-ui"
            >
              🎉 Here&apos;s 12% just for you!
            </text>
          </motion.g>
        </motion.g>

        {/* Sparkles */}
        <Sparkle x={62} y={83} delay={5.5} shouldAnimate={isInView} />
        <Sparkle x={72} y={80} delay={5.8} shouldAnimate={isInView} />
        <Sparkle x={67} y={88} delay={6.1} shouldAnimate={isInView} />
      </svg>
    </div>
  );
}
